import { useEffect, useRef, useState } from 'react';

export default function PricingSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="pricing" className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-purple-50 to-yellow-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-10 left-10 w-40 h-40 bg-pink-200 rounded-full opacity-20 blur-3xl animate-pulse"></div>
      <div className="absolute bottom-10 right-10 w-40 h-40 bg-purple-200 rounded-full opacity-20 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className={`text-center mb-10 sm:mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="inline-block bg-yellow-100 px-4 sm:px-6 py-2 rounded-full mb-4 sm:mb-6">
            <span className="text-yellow-600 font-bold text-xs sm:text-sm">料金プラン</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-800 mb-4 sm:mb-6 px-2">
            <span className="bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              シンプルで分かりやすい料金
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600">追加費用なし！安心の明朗会計</p>
        </div>

        {/* Pricing Card */}
        <div className={`max-w-2xl mx-auto transform transition-all duration-1000 delay-300 ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
          <div className="bg-gradient-to-br from-pink-100 via-purple-100 to-yellow-100 rounded-2xl sm:rounded-3xl p-1.5 sm:p-2 shadow-2xl">
            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-12">
              {/* Popular Badge */}
              <div className="text-center mb-6 sm:mb-8">
                <div className="inline-block bg-gradient-to-r from-pink-500 to-purple-500 text-white px-4 sm:px-8 py-2 sm:py-3 rounded-full font-black text-sm sm:text-lg shadow-lg animate-pulse">
                  🌟 一番人気のプラン 🌟
                </div>
              </div>

              {/* Price */}
              <div className="text-center mb-8 sm:mb-10">
                <div className="flex items-center justify-center space-x-2 mb-3 sm:mb-4">
                  <span className="text-lg sm:text-2xl line-through text-gray-500">¥15,000</span>
                  <span className="bg-pink-500 text-white px-3 sm:px-4 py-1 rounded-full font-bold text-xs sm:text-sm whitespace-nowrap">特別価格</span>
                </div>
                <div className="flex items-end justify-center space-x-2">
                  <span className="text-5xl sm:text-6xl md:text-7xl font-black bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                    ¥10,000
                  </span>
                  <span className="text-xl sm:text-2xl text-gray-600 font-bold mb-2 sm:mb-3">/月</span>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-3 sm:space-y-4 mb-8 sm:mb-10">
                {[
                  '週1回（月4回）のレッスン',
                  '梅田校での対面授業またはオンライン',
                  '非エンジニア目線の優しい指導',
                  'PCレンタル対応（追加費用なし）',
                  '保護者向け進捗レポート',
                  '質問し放題のサポート体制'
                ].map((feature, index) => (
                  <div 
                    key={index} 
                    className="flex items-center space-x-2 sm:space-x-3 transform transition-all duration-500 hover:translate-x-2"
                    style={{ transitionDelay: `${index * 0.1}s` }}
                  >
                    <div className="w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center bg-gradient-to-r from-pink-400 to-purple-400 rounded-full flex-shrink-0">
                      <span className="text-white font-bold text-sm sm:text-base">✓</span>
                    </div>
                    <span className="text-gray-700 text-sm sm:text-lg font-medium">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <a 
                href="#trial"
                className="block w-full bg-gradient-to-r from-pink-500 to-purple-500 text-white text-center px-6 sm:px-8 py-4 sm:py-5 rounded-full font-black text-lg sm:text-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <span className="flex items-center justify-center space-x-2 sm:space-x-3">
                  <span>🎉</span>
                  <span className="text-base sm:text-xl">今すぐ無料体験に申し込む</span>
                  <span>🎉</span>
                </span>
              </a>

              {/* Additional Info */}
              <div className="mt-6 sm:mt-8 text-center space-y-1 sm:space-y-2">
                <p className="text-xs sm:text-sm text-gray-600">
                  ✅ 入会金：無料（キャンペーン中）
                </p>
                <p className="text-xs sm:text-sm text-gray-600">
                  ✅ 教材費：無料
                </p>
                <p className="text-xs sm:text-sm text-gray-600">
                  ✅ いつでも退会可能
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Extra Benefits */}
        <div className={`mt-12 sm:mt-16 grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 transform transition-all duration-1000 delay-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 shadow-lg text-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300">
            <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">💻</div>
            <h4 className="font-bold text-base sm:text-lg text-gray-800 mb-2">PCレンタル無料</h4>
            <p className="text-gray-600 text-xs sm:text-sm">高額なPC購入不要！まずは気軽に始められます</p>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 shadow-lg text-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300">
            <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">🏠</div>
            <h4 className="font-bold text-base sm:text-lg text-gray-800 mb-2">オンライン対応</h4>
            <p className="text-gray-600 text-xs sm:text-sm">ご自宅からでも受講可能！送り迎え不要</p>
          </div>
          <div className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 shadow-lg text-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 sm:col-span-2 md:col-span-1">
            <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">❤️</div>
            <h4 className="font-bold text-base sm:text-lg text-gray-800 mb-2">親心サポート</h4>
            <p className="text-gray-600 text-xs sm:text-sm">保護者様の不安も一緒に解決します</p>
          </div>
        </div>
      </div>
    </section>
  );
}
